import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { LanguageContext } from '../../context/LanguageContext';
import { useLocation } from 'react-router-dom';

/**
 * Blog detay sayfaları için çeviri destekli SEO meta etiketlerini yöneten Helmet bileşeni
 * @param {Object} blog - Blog verisi
 * @param {Object} blogContent - Blog içeriği
 * @returns {JSX.Element} - Helmet bileşeni
 */
const BlogHelmet = ({ blog, blogContent }) => {
  const { currentLang } = useContext(LanguageContext);
  const location = useLocation();
  
  // Base URL
  const baseUrl = 'https://monaklinik.com';
  
  // Canonical ve hreflang URL'leri
  const canonicalUrl = `${baseUrl}${location.pathname}`;
  
  // Türkçe ve İngilizce URL'leri
  const trUrl = blog.urlTr ? `${baseUrl}/blog/${blog.urlTr}` : `${baseUrl}/blog/${blog.url}`;
  const enUrl = blog.url ? `${baseUrl}/en/blog/${blog.url}` : `${baseUrl}/en/blog/${blog.urlTr}`;
  
  // Meta description oluşturma
  let metaDescription = "";
  
  // Blog içeriğinden meta description oluştur
  if (blogContent && blogContent.content && Array.isArray(blogContent.content) && blogContent.content.length > 0) {
    // Rich text içeriğinden metin çıkarma
    let textContent = "";
    
    // Blog içeriğini işle
    blogContent.content.forEach(section => {
      if (section.__component === 'blog.text-section2' && section.text) {
        section.text.forEach(block => {
          if (block.children) {
            block.children.forEach(child => {
              if (child.text) {
                textContent += child.text + ' ';
              }
            });
          }
        });
      }
    });
    
    // Metin içeriği varsa, 155 karaktere kısalt
    if (textContent) {
      metaDescription = textContent.substring(0, 155).trim();
      if (textContent.length > 155) metaDescription += "...";
    }
  }
  
  // Eğer hala meta description yoksa, varsayılan oluştur
  if (!metaDescription && blogContent && blogContent.title) {
    metaDescription = currentLang === 'tr' 
      ? `Mona Klinik diş hekimlerinin ${blogContent.title} hakkında hazırladığı bilgilendirici makale. Diş sağlığınız için uzman önerileri.`
      : `Informative article about ${blogContent.title} prepared by Mona Clinic dentists. Expert recommendations for your dental health.`;
  } else if (!metaDescription) {
    // Title bile yoksa en basit varsayılan
    metaDescription = currentLang === 'tr'
      ? 'Mona Klinik diş hekimlerinin hazırladığı bilgilendirici makale. Diş sağlığınız için uzman önerileri.'
      : 'Informative article prepared by Mona Clinic dentists. Expert recommendations for your dental health.';
  }
  
  // Blog yayın ve güncelleme tarihlerini al
  const publishDate = blogContent?.publishedAt || blogContent?.createdAt || new Date().toISOString();
  const modifiedDate = blogContent?.updatedAt || publishDate;
  
  // Blog görselini al
  const blogImage = blog?.img || blogContent?.cover?.url || blogContent?.image || blogContent?.image2 || '';
  
  // Blog başlığını güvenli şekilde al
  const blogTitle = blogContent?.title || 'Blog';
  const fullTitle = `${blogTitle} | Mona Klinik`;
  
  // Schema.org yapısal veri işaretlemesi
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': blogTitle,
    'description': metaDescription,
    'image': blogImage,
    'datePublished': publishDate,
    'dateModified': modifiedDate,
    'author': {
      '@type': 'Person',
      'name': 'Dr. Samet Aksu'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Mona Klinik',
      'logo': {
        '@type': 'ImageObject',
        'url': `${baseUrl}/logo192.png`
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': canonicalUrl
    }
  };
  
  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang etiketleri */}
      <link rel="alternate" hreflang="tr" href={trUrl} />
      <link rel="alternate" hreflang="en" href={enUrl} />
      <link rel="alternate" hreflang="x-default" href={trUrl} />
      
      {/* Sosyal medya meta etiketleri - Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={blogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:locale" content={currentLang === 'tr' ? 'tr_TR' : 'en_US'} />
      <meta property="og:site_name" content="Mona Klinik" />
      <meta property="article:published_time" content={publishDate} />
      <meta property="article:modified_time" content={modifiedDate} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={blogImage} />
      
      {/* Schema.org yapısal veri işaretlemesi */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};

export default BlogHelmet; 